/**
 * How long he has practised today.
 *
 * WRITING COUNTS AS PRACTICE. It is in fact the main form of it — the
 * exercises drill what went wrong, but what is being learnt is writing, and
 * a child who writes for twenty minutes has practised more than one who
 * tapped through ten multiple-choice questions.
 *
 * Both are kept apart all the same (`Kind` below), because they are not the
 * same thing to look at: the figures show which is which, and the day's
 * measure counts both together.
 *
 * The round used to end after ten exercises. That is the wrong measure: ten
 * capitalisation sentences are done in two minutes, ten spellings with a long
 * sentence above them take three times as long. What is meant to be over at
 * some point is the SITTING, not a count.
 *
 * Nothing of this is shown. No clock, no "three minutes left", no bar that
 * fills up — whoever sees a timer practises against the timer. He only
 * notices that at some point it says: that is enough for today.
 *
 * Measured is the time AT the exercise, not the time the window stands open:
 * the tab lies in the background, somebody is called to dinner, the phone
 * locks. Every exercise contributes at most `MAX_PER_EXERCISE`, so an
 * interrupted sitting does not eat the whole day's measure at once.
 */

/** How long a sitting lasts before it says that is enough. */
export const DAILY_GOAL = 10 * 60 * 1000

/**
 * The most that a single exercise counts.
 *
 * Whoever puts the phone away in the middle would otherwise bring back an
 * hour. Ninety seconds is more than any of these exercises needs and short
 * enough that a break does not count as practice.
 */
const MAX_PER_EXERCISE = 90 * 1000

/**
 * How long a gap in the typing may be before it stops counting.
 *
 * Writing is not measured per exercise but per keystroke: whatever lies
 * between two of them counts, up to this. Whoever leaves the tab open and
 * does something else must not collect an afternoon.
 *
 * A minute, and that is deliberately not more: a child thinking about the
 * next sentence loses that thinking time, which is the smaller mistake. The
 * other way round the figure would say two hours where twenty minutes were
 * written, and then it says nothing at all.
 */
const MAX_GAP = 60 * 1000

const KEY = 'kritzl.practice'

/** What is remembered — for one day. */
interface Practice {
  /** The day, as YYYY-MM-DD in local time. */
  day: string
  /** Milliseconds practised on that day. */
  ms: number
}

/**
 * Today as a key.
 *
 * Local time, not UTC: whoever practises at eleven in the evening is
 * practising today, and a day that turns over at two in the morning would be
 * a strange one.
 */
export function today(now = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** How long he has practised today. Zero on a new day. */
export function practisedToday(): number {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return 0
    const p = JSON.parse(raw) as Partial<Practice>
    if (typeof p.day !== 'string' || typeof p.ms !== 'number') return 0
    // A different day starts at zero. The old value is not kept: what is
    // wanted is a habit, not a total score.
    return p.day === today() ? Math.max(0, p.ms) : 0
  } catch {
    // Private mode, blocked storage, a broken entry — practising works
    // without the measure, it then simply never ends by itself.
    return 0
  }
}

/** Has he done enough for today? */
export const enoughForToday = (): boolean => practisedToday() >= DAILY_GOAL

/**
 * Books the time of one exercise.
 *
 * @param since  When the exercise appeared, as `Date.now()`.
 * @returns The total for today, so the caller does not have to read it again.
 */
export function addPractice(since: number, kind: Kind = 'drill'): number {
  const spent = Math.min(Math.max(0, Date.now() - since), MAX_PER_EXERCISE)
  const total = practisedToday() + spent
  try {
    localStorage.setItem(KEY, JSON.stringify({ day: today(), ms: total } satisfies Practice))
  } catch {
    // See above — not being able to write is not a reason to stop.
  }
  // The same time into the history, so that no caller has to think of both
  // stores. They answer two different questions — see `practiceDays` below.
  addToDay(spent, kind)
  return total
}

// --- The days ---------------------------------------------------------------

/**
 * What was practised on earlier days.
 *
 * `practisedToday` above is the measure OF THE SITTING: it answers "is that
 * enough for today", and for that a single day is all it needs. This here is
 * the looking back — "5 of 7 days", the minutes of the week — and that needs
 * the days themselves.
 *
 * They are two different questions, so they are two different stores. Making
 * `practisedToday` read the history would tie the end of a sitting to a list
 * that may come down from another device mid-sitting.
 *
 * Kept as a map day → milliseconds. `MAX_DAYS` of them: the start screen
 * shows seven, the detail screen four weeks, and nothing asks for more.
 */
const DAYS_KEY = 'kritzl.days'

/** Forgets today's minutes and the days. For a device that changes hands. */
export function forgetPractice(): void {
  try {
    localStorage.removeItem(KEY)
    localStorage.removeItem(DAYS_KEY)
  } catch {}
}

/** How many days are kept. Four weeks plus a few, so a month is always full. */
const MAX_DAYS = 90

/** The two ways of practising. Writing is the main one. */
export type Kind = 'write' | 'drill'

/** What a day holds: milliseconds of each kind, and the words written. */
export interface Day {
  write: number
  drill: number
  /**
   * Words written without a red mark on them, on this day.
   *
   * Counted per DAY, not per text: a text is worked on over days, and a
   * counter on it would either be rewritten every time or count the same
   * words again. A day is closed by the time it is over.
   *
   * What it counts is what he really got right — not that he knows every one
   * of them. A short text of easy words scores like a hard one, and that is
   * the limit of this figure.
   */
  words?: number
  /**
   * Pages photographed that day.
   *
   * Not a duration — a photograph takes a moment, and the writing it captures
   * happened at school with no clock running here. It is a COUNT, and it
   * shows in the week as a mark rather than as height: a day with a
   * photograph did something for the app, even if nothing was typed.
   */
  photos?: number
}

export type Days = Record<string, Day>

/** Both together — what the day's measure and the week count. */
export const dayTotal = (d?: Day): number => (d ? d.write + d.drill : 0)

/**
 * What was practised per day.
 *
 * Reads the older shape too, where a day was a bare number: those days were
 * all exercises, because writing was not counted then. Without that branch
 * the history would look empty after the update.
 */
export function practiceDays(): Days {
  try {
    const raw = localStorage.getItem(DAYS_KEY)
    if (!raw) return {}
    const d = JSON.parse(raw) as unknown
    if (!d || typeof d !== 'object' || Array.isArray(d)) return {}
    const out: Days = {}
    for (const [day, v] of Object.entries(d as Record<string, unknown>)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) continue
      out[day] = asDay(v)
    }
    return out
  } catch {
    // Private mode, blocked storage, a broken entry — the looking back then
    // shows nothing, and practising works all the same.
    return {}
  }
}

/** One day out of whatever is stored — old shape or new. */
function asDay(v: unknown): Day {
  if (typeof v === 'number') return { write: 0, drill: Math.max(0, v) }
  if (v && typeof v === 'object') {
    const o = v as Partial<Day>
    return {
      write: typeof o.write === 'number' && o.write > 0 ? o.write : 0,
      drill: typeof o.drill === 'number' && o.drill > 0 ? o.drill : 0,
      ...(typeof o.words === 'number' && o.words > 0 ? { words: o.words } : {}),
      ...(typeof o.photos === 'number' && o.photos > 0 ? { photos: o.photos } : {}),
    }
  }
  return { write: 0, drill: 0 }
}

/**
 * Writes the days, keeping only the youngest `MAX_DAYS`.
 *
 * @returns true when it really got written.
 */
function writeDays(days: Days): boolean {
  const keep = Object.keys(days).sort().slice(-MAX_DAYS)
  const out: Days = {}
  for (const k of keep) out[k] = days[k]!
  try {
    localStorage.setItem(DAYS_KEY, JSON.stringify(out))
    return true
  } catch {
    return false
  }
}

/**
 * Books the time of one exercise on today's day.
 *
 * Called by `addPractice`, so no caller has to think of both stores.
 */
function addToDay(ms: number, kind: Kind): void {
  if (ms <= 0) return
  const days = practiceDays()
  const key = today()
  const d = days[key] ?? { write: 0, drill: 0 }
  days[key] = { ...d, [kind]: d[kind] + ms }
  writeDays(days)
}

/**
 * Takes over what another device has practised.
 *
 * `max()` per day, not a sum: two devices reporting the same day have usually
 * seen the same sitting — one offline, the other after the sync. Adding up
 * would double the minutes.
 *
 * @returns true when something new arrived.
 */
export function mergeDays(incoming: Record<string, unknown>): boolean {
  const here = practiceDays()
  let changed = false
  for (const [day, raw] of Object.entries(incoming)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) continue
    const there = asDay(raw)
    const mine = here[day] ?? { write: 0, drill: 0 }
    // Per FIELD, not per day: the PC writes, the phone drills and
    // photographs, and on a day with both the larger of each is the honest
    // picture.
    //
    // Every field, not only the two times. Photographs and clean words used
    // to be dropped here — a page photographed on the phone disappeared from
    // the week as soon as the PC synced the same day, because the merged day
    // was written back without them.
    const merged: Day = {
      write: Math.max(mine.write, there.write),
      drill: Math.max(mine.drill, there.drill),
    }
    const photos = Math.max(mine.photos ?? 0, there.photos ?? 0)
    const words = Math.max(mine.words ?? 0, there.words ?? 0)
    if (photos) merged.photos = photos
    if (words) merged.words = words
    if (
      merged.write === mine.write && merged.drill === mine.drill
      && photos === (mine.photos ?? 0) && words === (mine.words ?? 0)
    ) continue
    here[day] = merged
    changed = true
  }
  // Only report a change when one was really stored — with blocked storage
  // the read comes back empty every time, and everything would look new on
  // every sync.
  return changed ? writeDays(here) : false
}

/**
 * The last `n` days, oldest first — for the week on the start screen.
 *
 * Always exactly `n` entries, including the days with nothing on them: a gap
 * in the week is what the row is meant to show, so it may not be missing.
 */
export interface DayRow {
  day: string
  write: number
  drill: number
  /** Both kinds together. */
  ms: number
  /** Words written without a mark that day. */
  words: number
  /** Pages photographed that day. */
  photos: number
}

export function lastDays(n = 7, now = new Date()): DayRow[] {
  const days = practiceDays()
  const out: DayRow[] = []
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    const key = today(d)
    const e = days[key] ?? { write: 0, drill: 0 }
    out.push({
      day: key, write: e.write, drill: e.drill,
      ms: dayTotal(e), words: e.words ?? 0, photos: e.photos ?? 0,
    })
  }
  return out
}

// --- Writing ----------------------------------------------------------------

/**
 * Books the time spent writing.
 *
 * Not measured per exercise, because writing has none: what is counted is the
 * gap between two keystrokes, and only while it stays under `MAX_GAP`. Call
 * this on every input; it works out the rest.
 *
 * The state is a module variable, not storage: it is about the last keystroke
 * in THIS window, and a new tab starts a new stretch anyway.
 */
/**
 * When the last keystroke was, or `null` when no stretch is running.
 *
 * `null` and not `0`: with zero the first keystroke would be measured against
 * 1970 and the gap would be decades. `MAX_GAP` would throw that away, so it
 * would work — by accident, and only as long as nobody raises the limit.
 */
let lastKey: number | null = null

/**
 * One keystroke happened.
 *
 * The first one after a pause books nothing — there is nothing to book yet,
 * only a beginning. Every one after it books what lies between.
 */
export function typed(now = Date.now()): void {
  const before = lastKey
  lastKey = now
  if (before === null) return
  const gap = now - before
  // A gap longer than a minute is a break, not writing. The time is dropped
  // and the stretch starts again from here.
  if (gap <= 0 || gap > MAX_GAP) return
  addToDay(gap, 'write')
}

/**
 * Ends the stretch — before the window closes, on going to another screen.
 *
 * Without it the last stretch would be missing its tail: whoever writes his
 * final word and closes the lid has that word's time unbooked. It is at most
 * one gap, so the loss is small, but calling this is free.
 */
export function stoppedWriting(): void {
  lastKey = null
}

// --- Words written cleanly --------------------------------------------------

/**
 * How many words were written without a mark under them.
 *
 * The figure that says the most in the end: not "you have practised", but
 * "you wrote 214 words and 211 of them were right".
 *
 * Counted as a HIGH-WATER MARK per day, not added up. A text is typed, then
 * corrected, then typed on — adding the count on every check would count the
 * same sentence a dozen times over. What is kept is the largest clean count
 * seen that day, across all texts open that day.
 *
 * That undercounts whoever writes two texts on one day: the second one only
 * counts what it adds beyond the first. It is the error that is safe to make
 * — an inflated figure would be worth nothing.
 */
/** Notes that a page was photographed today. */
export function notePhoto(): void {
  const days = practiceDays()
  const key = today()
  const d = days[key] ?? { write: 0, drill: 0 }
  days[key] = { ...d, photos: (d.photos ?? 0) + 1 }
  writeDays(days)
}

export function noteCleanWords(count: number): void {
  if (!(count > 0)) return
  const days = practiceDays()
  const key = today()
  const d = days[key] ?? { write: 0, drill: 0 }
  if ((d.words ?? 0) >= count) return
  days[key] = { ...d, words: count }
  writeDays(days)
}

/**
 * Counts the words in a text that carry no mark.
 *
 * `marked` holds the words that stood underlined in red, lower case. A word
 * that was marked ONCE does not count as clean anywhere in the text, which is
 * the strict reading and the right one: he does not have it yet.
 */
export function cleanWords(text: string, marked: ReadonlySet<string>): number {
  // Words as a human counts them: letters, digits, apostrophes and hyphens
  // inside a word. Punctuation is no word.
  const words = text.match(/[\p{L}\p{N}]+(?:[’'-][\p{L}\p{N}]+)*/gu) ?? []
  if (!words.length) return 0
  return words.filter((w) => !marked.has(w.toLowerCase())).length
}
